import React, { useState, useEffect } from "react";
import Navigation from './Navigation';
import HeroSection from './sections/HeroSection';
import AboutSection from './sections/AboutSection';
import SkillsSection from './sections/SkillsSection';
import ProjectsSection from './sections/ProjectsSection';
import ContactSection from './sections/ContactSection';
import Footer from './Footer';
import { SectionId } from '../types';

const Portfolio: React.FC = () => {
  const [activeSection, setActiveSection] = useState<SectionId>("home");
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections: SectionId[] = ['home', 'about', 'skills', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100; // Offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    handleScroll(); // Run once on mount
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (elementId: SectionId): void => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(elementId);
    setIsMenuOpen(false);
  };

  return (
    <div className="relative min-h-screen bg-ink-950 text-white overflow-x-hidden">
      {/* Ambient light sources — emerald & gold glows on near-black */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute -top-48 -left-40 w-[44rem] h-[44rem] rounded-full bg-emerald-500/20 blur-[130px]" />
        <div className="absolute top-1/3 -right-48 w-[40rem] h-[40rem] rounded-full bg-gold-500/10 blur-[140px]" />
        <div className="absolute bottom-[-10rem] left-1/4 w-[42rem] h-[42rem] rounded-full bg-emerald-600/10 blur-[150px]" />
        <div className="absolute inset-0 vignette" />
      </div>

      <div className="relative z-10">
        <Navigation
          activeSection={activeSection}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isScrolled={isScrolled}
          scrollTo={scrollTo}
        />

        <HeroSection scrollTo={scrollTo} />
        <AboutSection />
        <SkillsSection />
        <ProjectsSection />
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default Portfolio;